/**
 * Backend URL resolution module.
 *
 * Priority order (highest to lowest):
 *  1. localStorage key `trade_model_backend_url`  — manual user override
 *  2. GitHub Gist (REACT_APP_GIST_ID baked at build time) — runtime discovery
 *  3. REACT_APP_API_URL env var                   — build-time configuration
 *  4. '/api'                                      — local dev / same-origin proxy
 */

const LS_KEY = 'trade_model_backend_url';
const GIST_ID = process.env.REACT_APP_GIST_ID;
const GIST_RAW_URL = process.env.REACT_APP_GIST_RAW_URL;
const BUILD_TIME_URL = process.env.REACT_APP_API_URL;

// Cached resolved URL (null = not yet resolved)
let _cachedUrl: string | null = null;
// Whether we've kicked off a fetch already
let _fetchPromise: Promise<string> | null = null;

/** Strip trailing slash for consistency. */
function normalise(url: string): string {
  return url.replace(/\/+$/, '');
}

/** The synchronous fallback used before resolution completes. */
function fallbackUrl(): string {
  const manual = localStorage.getItem(LS_KEY);
  if (manual) return normalise(manual);
  if (BUILD_TIME_URL) return normalise(BUILD_TIME_URL);
  return '/api';
}

/**
 * Fetch the tunnel URL from a Gist. Tries the raw URL first (simple, CDN-cached),
 * then falls back to the GitHub API.
 */
async function fetchFromGist(rawUrl?: string, gistId?: string): Promise<string | null> {
  // Try raw URL first (fastest, most reliable)
  if (rawUrl) {
    try {
      const resp = await fetch(rawUrl);
      if (resp.ok) {
        const data = await resp.json();
        if (typeof data.backend_url === 'string' && data.backend_url) {
          return normalise(data.backend_url) + '/api';
        }
      }
    } catch { /* fall through */ }
  }

  // Fallback: GitHub API
  if (gistId) {
    try {
      const resp = await fetch(`https://api.github.com/gists/${gistId}`, {
        headers: { Accept: 'application/vnd.github+json' },
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      const files: Record<string, { content: string }> = json.files || {};
      const firstFile = Object.values(files)[0];
      if (!firstFile?.content) return null;
      const data = JSON.parse(firstFile.content);
      if (typeof data.backend_url === 'string' && data.backend_url) {
        return normalise(data.backend_url) + '/api';
      }
    } catch { /* fall through */ }
  }

  return null;
}

/**
 * Resolve and cache the backend base URL.
 * Subsequent calls return the same promise (deduped).
 */
export async function getBackendUrl(): Promise<string> {
  if (_cachedUrl !== null) return _cachedUrl;
  if (_fetchPromise !== null) return _fetchPromise;

  _fetchPromise = (async (): Promise<string> => {
    // Manual override in localStorage takes precedence — no network call needed
    const manual = localStorage.getItem(LS_KEY);
    if (manual) {
      _cachedUrl = normalise(manual);
      return _cachedUrl;
    }

    // Try Gist if configured at build time
    if (GIST_RAW_URL || GIST_ID) {
      const gistUrl = await fetchFromGist(GIST_RAW_URL, GIST_ID);
      if (gistUrl) {
        _cachedUrl = gistUrl;
        return _cachedUrl;
      }
    }

    // Fall back to build-time env var or '/api'
    _cachedUrl = BUILD_TIME_URL ? normalise(BUILD_TIME_URL) : '/api';
    return _cachedUrl;
  })();

  return _fetchPromise;
}

/**
 * Synchronous accessor — returns the cached URL if already resolved,
 * otherwise returns the best available fallback immediately.
 */
export function getBackendUrlSync(): string {
  return _cachedUrl ?? fallbackUrl();
}

/**
 * Invalidate the cache and re-resolve. Useful after the user saves a new
 * manual override via the settings UI.
 */
export function invalidateBackendUrl(): void {
  _cachedUrl = null;
  _fetchPromise = null;
}

/**
 * Initialise the backend URL. Call once on app startup.
 * Resolves in the background; returns a promise that resolves when done.
 * Safe to call multiple times — deduped.
 */
export function initBackendUrl(): Promise<string> {
  return getBackendUrl();
}
